import { styled, useMediaQuery, useTheme } from '@mui/material';
import { memo, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AGENT_PROFILE_URL } from '../../../constants/common';
import { MainContext } from '../../../context';
import './Footer.scss';
import { CF_URL } from '../../../constants/common';
import Logo from '../../../assets/images/logo.webp';

const AgentDetails = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 32,
  padding: 64,
  maxWidth: 780,
  margin: '0 auto',
  backgroundColor: '#0F0F0F',
  borderRadius: 8,
  opacity: 0.8,
  border: '2px solid #2F2F2F',
  img: {
    borderRadius: '100%',
    height: 220,
    width: 220,
    objectFit: 'cover',
    objectPosition: 'top',
  },
  '& div.content': {
    width: 'calc(100% - 252px)',
    [theme.breakpoints.down('md')]: {
      textAlign: 'center',
      width: '100%',
    },
    '& div.details': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 8,
      [theme.breakpoints.down('md')]: {
        justifyContent: 'center',
      },
    },
    color: 'white',
    h3: {
      fontSize: 32,
      lineHeight: '42px',
      fontWeight: 600,
    },
    h4: {
      fontSize: 20,
      lineHeight: '28px',
      marginBottom: 32,
    },
    p: {
      fontSize: 17,
      lineHeight: '28px',
      wordBreak: 'break-word',
      a: {
        color: 'white',
      },
    },
  },

  [theme.breakpoints.down(992)]: {
    flexDirection: 'column',
  },
}));

const Footer = () => {
  const { isLoggedIn, subdomainAgent } = useContext(MainContext);

  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('md'));

  const ywhLogo = useMemo(() => {
    if (subdomainAgent) {
      if (subdomainAgent.brokerage.toLowerCase() === 'fathom') {
        return `${CF_URL}/web-assets/yourwayhome/logo/ywh-community-logo.webp`;
      } else {
        return `${CF_URL}/web-assets/yourwayhome/logo/ywh-community-agent-logo.webp`;
      }
    }
    return `${CF_URL}/web-assets/yourwayhome/logo/ywh-community-logo.webp`;
  }, [subdomainAgent]);

  return (
    // <!-- Footer Begin V2 -->
    <footer className="footer-section" style={{ position: 'relative', zIndex: 4 }}>
      <div className="footer">
        <div className="footer__wrapper">
          <section className="header">
            <h2>Experience the joy of homeownership, with us.</h2>
            <div className="header__hr"></div>
            <p>
              Let us guide you through the mortgage process and help you find the best loan option for your unique
              needs.
            </p>
          </section>
          <div className="footer__hr"></div>
          <section className="footer__ywh-logo">
            <div>
              <img src={Logo} alt="The New Home Collection, LLC" />
            </div>
          </section>
          {!!subdomainAgent && (
            <section>
              <AgentDetails className="subdomain-agent-footer">
                <img src={`${AGENT_PROFILE_URL}${subdomainAgent?.photo}`} alt="" />
                <div className="content">
                  <h3>{subdomainAgent.first_name + ' ' + subdomainAgent.last_name}</h3>
                  <h4>{subdomainAgent.brokerage}</h4>
                  {subdomainAgent.mobile_phone && (
                    <div className="details">
                      <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                        <mask id="path-1-inside-1_148_911" fill="white">
                          <path d="M10.8826 20.7582C16.3437 20.7582 20.8828 16.2287 20.8828 10.758C20.8828 5.29694 16.3337 0.757812 10.8726 0.757812C5.40228 0.757812 0.882812 5.29694 0.882812 10.758C0.882812 16.2287 5.4119 20.7582 10.8826 20.7582ZM10.883 19.0917C6.2548 19.0917 2.55816 15.3854 2.55816 10.758C2.55816 6.13984 6.24476 2.42437 10.8726 2.42437C15.4903 2.42437 19.2058 6.14026 19.2158 10.758C19.2255 15.3858 15.5 19.0917 10.8822 19.0917M8.52917 13.0721C10.8132 15.3662 13.7255 16.8169 15.451 15.101C15.5121 15.0406 15.5678 14.9751 15.6175 14.9052C16.0684 14.4053 16.196 13.7385 15.6175 13.3072C15.1666 12.9835 14.6863 12.6505 13.8723 12.0816C13.3038 11.68 12.9311 11.7775 12.4902 12.2188L12.0978 12.6011C11.9803 12.709 11.7941 12.6994 11.6665 12.6208C11.3235 12.415 10.745 11.954 10.1957 11.4052C9.65653 10.8655 9.16668 10.2581 8.98011 9.93436C8.91151 9.82644 8.86257 9.64991 8.99977 9.52274L9.38211 9.1107C9.82343 8.65014 9.92132 8.29708 9.5097 7.71855L8.3037 6.01267C7.89166 5.43414 7.29389 5.54207 6.63672 6.03233C6.58778 6.07165 6.54887 6.11097 6.50955 6.14988C4.78401 7.87542 6.24476 10.7873 8.52917 13.0717" />
                        </mask>
                        <path
                          d="M10.8826 20.7582C16.3437 20.7582 20.8828 16.2287 20.8828 10.758C20.8828 5.29694 16.3337 0.757812 10.8726 0.757812C5.40228 0.757812 0.882812 5.29694 0.882812 10.758C0.882812 16.2287 5.4119 20.7582 10.8826 20.7582ZM10.883 19.0917C6.2548 19.0917 2.55816 15.3854 2.55816 10.758C2.55816 6.13984 6.24476 2.42437 10.8726 2.42437C15.4903 2.42437 19.2058 6.14026 19.2158 10.758C19.2255 15.3858 15.5 19.0917 10.8822 19.0917M8.52917 13.0721C10.8132 15.3662 13.7255 16.8169 15.451 15.101C15.5121 15.0406 15.5678 14.9751 15.6175 14.9052C16.0684 14.4053 16.196 13.7385 15.6175 13.3072C15.1666 12.9835 14.6863 12.6505 13.8723 12.0816C13.3038 11.68 12.9311 11.7775 12.4902 12.2188L12.0978 12.6011C11.9803 12.709 11.7941 12.6994 11.6665 12.6208C11.3235 12.415 10.745 11.954 10.1957 11.4052C9.65653 10.8655 9.16668 10.2581 8.98011 9.93436C8.91151 9.82644 8.86257 9.64991 8.99977 9.52274L9.38211 9.1107C9.82343 8.65014 9.92132 8.29708 9.5097 7.71855L8.3037 6.01267C7.89166 5.43414 7.29389 5.54207 6.63672 6.03233C6.58778 6.07165 6.54887 6.11097 6.50955 6.14988C4.78401 7.87542 6.24476 10.7873 8.52917 13.0717"
                          fill="#636363"
                        />
                        <path
                          d="M19.2158 10.758L20.2158 10.7559V10.7558L19.2158 10.758ZM15.451 15.101L14.7483 14.3895L14.7459 14.3919L15.451 15.101ZM15.6175 14.9052L14.875 14.2354L14.836 14.2785L14.8024 14.3259L15.6175 14.9052ZM15.6175 13.3072L16.2152 12.5055L16.208 12.5001L16.2007 12.4949L15.6175 13.3072ZM13.8723 12.0816L13.2953 12.8983L13.2995 12.9012L13.8723 12.0816ZM12.4902 12.2188L13.1881 12.9351L13.1976 12.9255L12.4902 12.2188ZM12.0978 12.6011L12.7741 13.3377L12.7851 13.3277L12.7957 13.3173L12.0978 12.6011ZM11.6665 12.6208L12.1913 11.7694L12.181 11.7633L11.6665 12.6208ZM10.1957 11.4052L9.48835 12.112L9.4889 12.1125L10.1957 11.4052ZM8.98011 9.93436L9.84656 9.43509L9.83571 9.41626L9.82404 9.39791L8.98011 9.93436ZM8.99977 9.52274L9.67954 10.2562L9.70718 10.2306L9.73281 10.2029L8.99977 9.52274ZM9.38211 9.1107L8.66008 8.41884L8.65454 8.42463L8.64908 8.43051L9.38211 9.1107ZM9.5097 7.71855L8.69314 8.29583L8.69489 8.29829L9.5097 7.71855ZM8.3037 6.01267L9.12026 5.43539L9.11823 5.43255L8.3037 6.01267ZM6.63672 6.03233L6.03876 5.2308L6.02438 5.24153L6.0104 5.25276L6.63672 6.03233ZM6.50955 6.14988L5.80623 5.43899L5.80244 5.44277L6.50955 6.14988ZM10.8826 21.7582C16.8954 21.7582 21.8828 16.7816 21.8828 10.758H19.8828C19.8828 15.6758 15.792 19.7582 10.8826 19.7582V21.7582ZM21.8828 10.758C21.8828 4.74335 16.8846 -0.242188 10.8726 -0.242188V1.75781C15.7827 1.75781 19.8828 5.85052 19.8828 10.758H21.8828ZM10.8726 -0.242188C4.84804 -0.242188 -0.117188 4.74661 -0.117188 10.758H1.88281C1.88281 5.84726 5.95651 1.75781 10.8726 1.75781V-0.242188ZM-0.117188 10.758C-0.117188 16.781 4.85956 21.7582 10.8826 21.7582V19.7582C5.96424 19.7582 1.88281 15.6765 1.88281 10.758H-0.117188ZM10.883 18.0917C6.80853 18.0917 3.55816 14.8346 3.55816 10.758H1.55816C1.55816 15.9363 5.70107 20.0917 10.883 20.0917V18.0917ZM3.55816 10.758C3.55816 6.68835 6.80081 3.42437 10.8726 3.42437V1.42437C5.68871 1.42437 1.55816 5.59133 1.55816 10.758H3.55816ZM10.8726 3.42437C14.9376 3.42437 18.207 6.69254 18.2158 10.7602L20.2158 10.7558C20.2046 5.58798 16.0431 1.42437 10.8726 1.42437V3.42437ZM18.2158 10.7601C18.2243 14.8313 14.9504 18.0917 10.8822 18.0917V20.0917C16.0495 20.0917 20.2266 15.9403 20.2158 10.7559L18.2158 10.7601ZM7.82052 13.7777C9.02211 14.9846 10.4447 16.0301 11.8659 16.533C13.3044 17.042 14.922 17.0373 16.1561 15.81L14.7459 14.3919C14.2545 14.8805 13.5531 15.0085 12.5331 14.6475C11.4958 14.2806 10.3202 13.4537 9.23783 12.3666L7.82052 13.7777ZM16.1537 15.8125C16.256 15.7114 16.3493 15.6017 16.4326 15.4845L14.8024 14.3259C14.7862 14.3486 14.7681 14.3699 14.7483 14.3895L16.1537 15.8125ZM16.36 15.575C16.6891 15.2102 16.9696 14.7114 17.002 14.1367C17.0374 13.5075 16.7646 12.9151 16.2152 12.5055L15.0198 14.109C15.049 14.1307 15.0373 14.1304 15.0221 14.0984C15.0067 14.0661 15.0044 14.0365 15.0051 14.0242C15.0056 14.0162 15.0061 14.0336 14.9859 14.0759C14.966 14.118 14.9306 14.1737 14.875 14.2354L16.36 15.575ZM16.2007 12.4949C15.743 12.1663 15.2543 11.8274 14.4451 11.2619L13.2995 12.9012C14.1183 13.4735 14.5901 13.8006 15.0343 14.1195L16.2007 12.4949ZM14.4493 11.2648C14.0614 10.9908 13.5717 10.7666 13.0008 10.8429C12.4542 10.916 12.056 11.2385 11.7827 11.512L13.1976 12.9255C13.2348 12.8884 13.265 12.8611 13.2891 12.8412C13.3131 12.8214 13.3283 12.8113 13.3351 12.8071C13.3466 12.8 13.3207 12.8179 13.2659 12.8253C13.24 12.8287 13.2156 12.8287 13.195 12.8265C13.1749 12.8243 13.1638 12.8207 13.1634 12.8206C13.1633 12.8206 13.1661 12.8215 13.1718 12.824C13.1775 12.8264 13.1859 12.8304 13.1972 12.8364C13.2202 12.8487 13.2526 12.8682 13.2953 12.8983L14.4493 11.2648ZM11.7923 11.5026L11.3999 11.8849L12.7957 13.3173L13.1881 12.935L11.7923 11.5026ZM11.4215 11.8645C11.5749 11.7236 11.749 11.683 11.8697 11.681C11.985 11.6792 12.094 11.7096 12.1912 11.7695L11.1418 13.472C11.5322 13.7127 12.235 13.8328 12.7741 13.3377L11.4215 11.8645ZM12.181 11.7633C11.932 11.6139 11.4179 11.2128 10.9026 10.6978L9.4889 12.1125C10.072 12.6952 10.715 13.2161 11.152 13.4783L12.181 11.7633ZM10.9031 10.6983C10.3901 10.1849 9.96987 9.6491 9.84656 9.43509L8.11367 10.4336C8.36349 10.8672 8.92294 11.5461 9.48835 12.112L10.9031 10.6983ZM9.82404 9.39791C9.85385 9.4448 9.90426 9.54 9.91153 9.68604C9.91553 9.76649 9.90622 9.86878 9.86473 9.97869C9.82209 10.0917 9.75542 10.1858 9.67954 10.2562L8.32001 8.78931C8.00737 9.07907 7.89758 9.45564 7.914 9.78549C7.92859 10.0786 8.03778 10.316 8.13618 10.4708L9.82404 9.39791ZM9.73281 10.2029L10.1151 9.7909L8.64908 8.43051L8.26674 8.84255L9.73281 10.2029ZM10.1041 9.80257C10.3626 9.53286 10.6869 9.13361 10.7575 8.58699C10.832 8.01033 10.5971 7.52197 10.3245 7.13882L8.69489 8.29829C8.76195 8.39254 8.77858 8.43693 8.77978 8.44042C8.77979 8.44046 8.77876 8.43748 8.77741 8.43167C8.77605 8.42582 8.77445 8.41747 8.77322 8.40689C8.77065 8.38467 8.77042 8.35844 8.774 8.33074C8.78152 8.2725 8.8005 8.24612 8.79097 8.26185C8.7856 8.2707 8.77389 8.28836 8.75184 8.31579C8.72979 8.34321 8.70004 8.37714 8.66008 8.41884L10.1041 9.80257ZM10.3263 7.14128L9.12025 5.4354L7.48715 6.58994L8.69315 8.29583L10.3263 7.14128ZM9.11823 5.43255C8.73571 4.89547 8.16913 4.58592 7.50589 4.62329C6.93464 4.65547 6.4293 4.93945 6.03876 5.2308L7.23468 6.83386C7.35736 6.74233 7.45553 6.68524 7.52841 6.65229C7.60199 6.61903 7.62956 6.61949 7.61838 6.62012C7.59883 6.62122 7.55376 6.61724 7.50631 6.59296C7.46278 6.57069 7.46146 6.55389 7.48917 6.59279L9.11823 5.43255ZM6.0104 5.25276C5.91316 5.33089 5.83556 5.40999 5.80624 5.439L7.21287 6.86075C7.22529 6.84846 7.23165 6.84212 7.24011 6.83381C7.2474 6.82664 7.25236 6.82185 7.2562 6.81822C7.26342 6.81141 7.26484 6.81045 7.26304 6.8119L6.0104 5.25276ZM5.80244 5.44277C4.56815 6.67707 4.56373 8.29613 5.07293 9.73486C5.57582 11.1558 6.62117 12.5779 7.82206 13.7788L9.23628 12.3646C8.15276 11.2811 7.32553 10.1051 6.95833 9.06758C6.59743 8.04788 6.72541 7.34823 7.21666 6.85698L5.80244 5.44277Z"
                          fill="#636363"
                          mask="url(#path-1-inside-1_148_911)"
                        />
                      </svg>
                      <p>
                        <a href={`tel:${subdomainAgent.mobile_phone}`}>{subdomainAgent.mobile_phone}</a>
                      </p>
                    </div>
                  )}
                  {subdomainAgent.user.email && (
                    <div className="details" style={{ paddingBottom: 12, borderBottom: '1px solid #494949' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21" fill="none">
                        <path
                          d="M1.3066 5.3393C0.947241 5.13331 0.488933 5.25764 0.282942 5.617C0.0769512 5.97636 0.201283 6.43467 0.560644 6.64066L1.3066 5.3393ZM8.72281 10.4548L9.09962 9.80636L9.09579 9.80416L8.72281 10.4548ZM13.0201 10.4548L12.6479 9.8037L12.6433 9.80637L13.0201 10.4548ZM21.2031 6.64111C21.5627 6.43555 21.6876 5.97739 21.482 5.61778C21.2765 5.25817 20.8183 5.13329 20.4587 5.33885L21.2031 6.64111ZM5.20714 2.31836H16.5585V0.818359H5.20714V2.31836ZM16.5585 2.31836C18.5325 2.31836 20.1328 3.91864 20.1328 5.89268H21.6328C21.6328 3.09021 19.361 0.818359 16.5585 0.818359V2.31836ZM20.1328 5.89268V15.6224H21.6328V5.89268H20.1328ZM20.1328 15.6224C20.1328 17.5965 18.5325 19.1967 16.5585 19.1967V20.6967C19.361 20.6967 21.6328 18.4249 21.6328 15.6224H20.1328ZM16.5585 19.1967H5.20714V20.6967H16.5585V19.1967ZM5.20714 19.1967C3.23309 19.1967 1.63281 17.5965 1.63281 15.6224H0.132812C0.132812 18.4249 2.40466 20.6967 5.20714 20.6967V19.1967ZM1.63281 15.6224V5.89268H0.132812V15.6224H1.63281ZM1.63281 5.89268C1.63281 3.91864 3.23309 2.31836 5.20714 2.31836V0.818359C2.40466 0.818359 0.132812 3.09021 0.132812 5.89268H1.63281ZM0.560644 6.64066L8.34983 11.1055L9.09579 9.80416L1.3066 5.3393L0.560644 6.64066ZM8.34601 11.1033C9.11304 11.549 9.98435 11.7838 10.8715 11.7838V10.2838C10.2491 10.2838 9.63776 10.1191 9.09961 9.80637L8.34601 11.1033ZM10.8715 11.7838C11.7586 11.7838 12.6299 11.549 13.3969 11.1033L12.6433 9.80637C12.1052 10.1191 11.4939 10.2838 10.8715 10.2838V11.7838ZM13.3923 11.106L21.2031 6.64111L20.4587 5.33885L12.6479 9.80372L13.3923 11.106Z"
                          fill="#636363"
                        />
                      </svg>
                      <p>
                        <a href={`mailto:${subdomainAgent.user.email}`}>{subdomainAgent.user.email}</a>
                      </p>
                    </div>
                  )}
                  {subdomainAgent.company_website && (
                    <div className="details" style={{ marginTop: 8 }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                        <path
                          d="M15.0928 11.9309C15.0928 12.7709 15.3318 13.1059 15.9568 13.1059C17.3498 13.1059 18.2368 11.3309 18.2368 8.37892C18.2368 3.86692 14.9488 1.70692 10.8438 1.70692C6.62081 1.70692 2.77981 4.53892 2.77981 9.89092C2.77981 15.0029 6.13981 17.7869 11.2998 17.7869C13.0518 17.7869 14.2278 17.5949 16.0268 16.9949L16.4128 18.6019C14.6368 19.1789 12.7388 19.3459 11.2758 19.3459C4.50781 19.3459 0.882812 15.6259 0.882812 9.88992C0.882812 4.10592 5.08381 0.169922 10.8678 0.169922C16.8918 0.169922 20.0828 3.76992 20.0828 8.18592C20.0828 11.9299 18.9078 14.7859 15.2118 14.7859C13.5308 14.7859 12.4278 14.1139 12.2838 12.6249C11.8518 14.2809 10.6998 14.7859 9.13881 14.7859C7.05081 14.7859 5.29881 13.1769 5.29881 9.93792C5.29881 6.67392 6.83581 4.65792 9.59581 4.65792C11.0598 4.65792 11.9718 5.23392 12.3778 6.14592L13.0748 4.87392H15.0908V11.9309H15.0928ZM12.1418 8.76292C12.1418 7.44392 11.1568 6.89092 10.3408 6.89092C9.45281 6.89092 8.46981 7.60992 8.46981 9.72292C8.46981 11.4029 9.21381 12.3389 10.3408 12.3389C11.1328 12.3389 12.1418 11.8349 12.1418 10.4429V8.76292Z"
                          fill="#636363"
                        />
                      </svg>
                      <p>
                        <a
                          href={`${subdomainAgent.company_website.replace('https://', '//').replace('http://', '//')}`}
                          target="_blank"
                        >
                          {subdomainAgent.company_website}
                        </a>
                      </p>
                    </div>
                  )}
                  {subdomainAgent.office_address && (
                    <div className="details">
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                        <path
                          d="M10.9998 2.25777C10.2042 2.25777 9.4411 2.57384 8.87849 3.13645C8.31588 3.69906 7.99981 4.46212 7.99981 5.25777C7.99981 6.05342 8.31588 6.81648 8.87849 7.37909C9.4411 7.9417 10.2042 8.25777 10.9998 8.25777C11.7955 8.25777 12.5585 7.9417 13.1211 7.37909C13.6837 6.81648 13.9998 6.05342 13.9998 5.25777C13.9998 4.46212 13.6837 3.69906 13.1211 3.13645C12.5585 2.57384 11.7955 2.25777 10.9998 2.25777ZM5.99981 5.25777C6.00001 4.31174 6.26858 3.38518 6.77435 2.58569C7.28011 1.78621 8.00231 1.14662 8.85707 0.741198C9.71183 0.335778 10.6641 0.18117 11.6032 0.29533C12.5423 0.409491 13.4298 0.787735 14.1625 1.38613C14.8952 1.98453 15.4431 2.77852 15.7426 3.67589C16.0421 4.57326 16.0809 5.53719 15.8544 6.45571C15.628 7.37424 15.1456 8.20967 14.4633 8.86497C13.7809 9.52028 12.9267 9.96856 11.9998 10.1578V16.2578H9.99981V10.1578C8.87062 9.92689 7.8558 9.31314 7.12696 8.42031C6.39811 7.52747 5.99995 6.41032 5.99981 5.25777ZM2.10481 10.2578H6.99981V12.2578H3.89481L3.11681 19.2578H18.8828L18.1048 12.2578H14.9998V10.2578H19.8948L21.1168 21.2578H0.882812L2.10481 10.2578Z"
                          fill="#636363"
                        />
                      </svg>
                      <p>{subdomainAgent.office_address}</p>
                    </div>
                  )}
                </div>
              </AgentDetails>
            </section>
          )}
          <section className="contents">
            {!subdomainAgent ? (
              <div className="contact-details">
                <div className="contents__col">
                  <div className="contacts" style={{ alignItems: smScreen ? 'center' : 'start' }}>
                    <div className="contacts__info">
                      <div>
                        <img src={`${CF_URL}/web-assets/yourwayhome/footer/envelope.webp`} alt="" />
                      </div>
                      <div className="text">ClareGleason108@gmail.com</div>
                    </div>
                    <div className="contacts__info">
                      <div>
                        <img src={`${CF_URL}/web-assets/yourwayhome/footer/phone.webp`} alt="" />
                      </div>
                      <div className="text">303-931-8111</div>
                    </div>
                    <div className="socmed">
                      <div>
                        <a href="https://www.facebook.com/yourwayhome/" target="_blank" rel="noreferrer">
                          <img src={`${CF_URL}/web-assets/yourwayhome/footer/facebook.webp`} alt="Facebook Logo" />
                        </a>
                      </div>
                      <div>
                        <a
                          href="https://www.youtube.com/channel/UChybnCtRAJ_LFMZOszFJRgg"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <img src={`${CF_URL}/web-assets/yourwayhome/footer/youtube.webp`} alt="YouTube Logo" />
                        </a>
                      </div>
                      <div>
                        <a href="https://www.linkedin.com/company/your-way-home" target="_blank" rel="noreferrer">
                          <img src={`${CF_URL}/web-assets/yourwayhome/footer/linkedin.webp`} alt="LinkedIn Logo" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                {!smScreen && (
                  <img
                    style={{ margin: '0 auto' }}
                    width={84}
                    src={`${CF_URL}/web-assets/yourwayhome/footer/equal-housing-lender.webp`}
                    alt="Equal Housing Lender Logo"
                    srcSet={`
                    ${CF_URL}/web-assets/yourwayhome/footer/equal-housing-lender@2x.webp 2x,
                    ${CF_URL}/web-assets/yourwayhome/footer/equal-housing-lender@3x.webp 3x
                  `}
                  />
                )}
              </div>
            ) : (
              !smScreen && (
                <div className="partner">
                  <img
                    style={{ margin: '0 auto' }}
                    width={84}
                    src={`${CF_URL}/web-assets/yourwayhome/footer/equal-housing-lender.webp`}
                    alt="Equal Housing Lender Logo"
                    srcSet={`
                    ${CF_URL}/web-assets/yourwayhome/footer/equal-housing-lender@2x.webp 2x,
                    ${CF_URL}/web-assets/yourwayhome/footer/equal-housing-lender@3x.webp 3x
                  `}
                  />
                  <a href="https://www.nmlsconsumeraccess.org/" target="_blank" rel="noreferrer">
                    <button type="button">NMLS Consumer Access Portal</button>
                  </a>
                </div>
              )
            )}
            <div className="partner" style={{ alignItems: smScreen ? 'center' : 'left' }}>
              <div className="partner__details" style={{ textAlign: smScreen ? 'center' : 'left' }}>
                <p className="partner__heading">ENCOMPASS LENDING GROUP, LP</p>
                <div className="partner__text">
                  23108 Seven Meadows Pkwy
                  <br />
                  Suite 100, Katy, TX 77494
                  <br />
                  (855) 353-5363
                </div>
                <div className="partner__text">NMLS # 292897</div>
                <div className="partner__text">Company State License # AZMB - 0944059</div>
              </div>
              {(!subdomainAgent || smScreen) && (
                <a href="https://www.nmlsconsumeraccess.org/" target="_blank" rel="noreferrer">
                  <button type="button">NMLS Consumer Access Portal</button>
                </a>
              )}
            </div>
          </section>
          <div className="footer__hr"></div>
          <section className="footer__notes">
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: smScreen ? 'column' : 'row',
                alignItems: 'center',
                width: '100%',
                gap: smScreen ? 32 : 0,
              }}
            >
              {smScreen && (
                <img
                  src={`${CF_URL}/web-assets/yourwayhome/footer/equal-housing-lender.webp`}
                  alt="Equal Housing Lender Logo"
                  srcSet={`
                  ${CF_URL}/web-assets/yourwayhome/footer/equal-housing-lender@2x.webp 2x,
                  ${CF_URL}/web-assets/yourwayhome/footer/equal-housing-lender@3x.webp 3x
									`}
                />
              )}
              <div className="flex-login flex-col">
                {!isLoggedIn && !subdomainAgent && (
                  <div style={{ display: 'flex', gap: 16 }}>
                    <Link to="/admin/login">
                      <button
                        type="button"
                        className="btn-border"
                        style={{
                          fontWeight: 400,
                          fontSize: 16,
                          lineHeight: '24px',
                          padding: '8px 32px',
                          borderRadius: 18,
                          marginBottom: 16,
                        }}
                      >
                        Agent Login
                      </button>
                    </Link>
                    <Link to="/user/login">
                      <button
                        type="button"
                        className="btn-border"
                        style={{
                          fontWeight: 400,
                          fontSize: 16,
                          lineHeight: '24px',
                          padding: '8px 32px',
                          borderRadius: 18,
                          marginBottom: 16,
                        }}
                      >
                        User Login
                      </button>
                    </Link>
                  </div>
                )}
                <div className="copyright" style={{ color: '#757575' }}>
                  © 2025 The New Home Collection
                </div>
                <div className="flex-login gap-8" style={{ color: '#757575' }}>
                  <Link to="/policy?tab=PrivacyPolicy" style={{ lineHeight: '18px', color: '#757575' }}>
                    Privacy Policy
                  </Link>
                  |
                  <Link to="/policy?tab=TermsOfService" style={{ lineHeight: '18px', color: '#757575' }}>
                    Terms of Service
                  </Link>
                  |
                  <Link to="https://www.yourwayhome.com/sitemap.xml" style={{ lineHeight: '18px', color: '#757575' }}>
                    Sitemap
                  </Link>
                  |
                  <Link to="/accessibility" style={{ lineHeight: '18px', color: '#757575' }}>
                    Accessibility
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </footer>
    // <!-- Footer End V2 -->
  );
};
export default memo(Footer);
